// Analyse automatique d'une opération (MVP : heuristique par mots-clés + règles simples).
// Logique métier isolée du rendu. Reflète le moteur de règles côté serveur pour l'aide à la décision.
import { computeCriticite } from "@/lib/criticite";

const TRAVAIL_PERMIS = {
  point_chaud: "feu", hauteur: "hauteur", toiture: "toiture", espace_confine: "espace_confine",
  electrique: "electrique", maintenance_machine: "consignation", levage: "levage", fouille: "fouille",
};
const PERMIS_LABEL = {
  feu: "Permis de feu", hauteur: "Permis travail en hauteur", toiture: "Permis intervention toiture",
  espace_confine: "Permis espace confiné", electrique: "Permis travaux électriques",
  consignation: "Consignation toutes énergies", levage: "Permis de levage", fouille: "Permis de fouille",
};

// Détection de types de travaux à partir du texte libre.
const KEYWORDS = [
  { re: /toiture|étanch|couvertur/i, travaux: ["toiture", "hauteur"], note: "Travaux en toiture → travail en hauteur." },
  { re: /hauteur|échafaud|nacelle|pemp|harnais/i, travaux: ["hauteur"], note: "Travail en hauteur détecté." },
  { re: /grue|levage|manutention|élingu|palan/i, travaux: ["levage"], note: "Opération de levage détectée." },
  { re: /électr|consignation|tableau|armoire|vat/i, travaux: ["electrique"], note: "Intervention électrique → consignation." },
  { re: /soudure|meulage|découpe|flamme|point chaud|oxycoup/i, travaux: ["point_chaud"], note: "Point chaud → permis de feu." },
  { re: /espace confiné|cuve|silo|cellule|galerie|réservoir/i, travaux: ["espace_confine"], note: "Espace confiné détecté." },
  { re: /chimique|solvant|peinture|résine|décapa/i, travaux: ["chimique"], note: "Produits chimiques détectés." },
  { re: /fouille|terrassement|tranchée|excavation/i, travaux: ["fouille"], note: "Fouille / terrassement détecté." },
  { re: /poussi|ponçage|sablage/i, travaux: ["poussiereux"], note: "Travaux poussiéreux détectés." },
  { re: /maintenance machine|convoyeur|mécani/i, travaux: ["maintenance_machine"], note: "Maintenance machine → consignation." },
];

export function analyseTexte(texte = "") {
  const travaux = new Set();
  const notes = [];
  KEYWORDS.forEach((k) => {
    if (k.re.test(texte)) { k.travaux.forEach((t) => travaux.add(t)); notes.push(k.note); }
  });
  return { travauxSuggeres: [...travaux], notes };
}

// Analyse globale en temps réel de l'opération.
export function analyseOperation(form, zonesSelectionnees = [], labels = {}) {
  const crit = computeCriticite(form, zonesSelectionnees);
  const travaux = form.natureTravaux || [];

  const permisRequis = [];
  travaux.forEach((t) => {
    const p = TRAVAIL_PERMIS[t];
    if (p && !permisRequis.includes(p)) permisRequis.push(p);
    if (t === "electrique" && !permisRequis.includes("consignation")) permisRequis.push("consignation");
  });

  const risques = [];
  travaux.forEach((t) => risques.push((labels.travaux && labels.travaux[t]) || t));
  zonesSelectionnees.forEach((z) => (z.attributs || []).forEach((a) => {
    const l = (labels.attrs && labels.attrs[a]) || a;
    if (!risques.includes(l)) risques.push(l);
  }));

  const documentsManquants = [];
  if (form.entrepriseExterieure) documentsManquants.push("Plan de prévention à établir (art. R4512-6)");
  if (form.sousTraitance) documentsManquants.push("Attestation de vigilance du/des sous-traitant(s)");
  if (travaux.includes("point_chaud")) documentsManquants.push("Permis de feu à délivrer");
  if (travaux.includes("espace_confine")) documentsManquants.push("Certificat d'aptitude + permis espace confiné");
  if (travaux.includes("electrique")) documentsManquants.push("Habilitations électriques des intervenants");
  if (travaux.includes("hauteur") || travaux.includes("toiture")) documentsManquants.push("Attestation travail en hauteur");

  const vigilance = [];
  if (form.siteOccupe) vigilance.push("Site occupé : coordination des flux et information des occupants.");
  if (form.presencePublicOuCollaborateurs) vigilance.push("Présence de public / collaborateurs : balisage et périmètre de sécurité.");
  if (form.sousTraitance) vigilance.push("Sous-traitance : chaîne de responsabilité et documents à jour.");
  if ((form.zones || []).length > 1) vigilance.push("Plusieurs zones concernées : risque de coactivité à analyser.");

  const conflits = [];
  if (travaux.includes("levage") && (travaux.includes("hauteur") || travaux.includes("toiture")))
    conflits.push("Levage + travail en hauteur : phasage horaire ou exclusion de zone à prévoir.");
  if (travaux.includes("point_chaud") && zonesSelectionnees.some((z) => (z.attributs || []).includes("produits_inflammables")))
    conflits.push("Point chaud en zone à produits inflammables : mesures compensatoires obligatoires.");
  if (travaux.includes("point_chaud") && zonesSelectionnees.some((z) => (z.attributs || []).includes("atex")))
    conflits.push("Point chaud en zone ATEX : mesure d'atmosphère + inertage.");

  const actions = [];
  if (documentsManquants.length) actions.push("Réunir les documents et habilitations manquants avant l'inspection commune.");
  if (form.entrepriseExterieure) actions.push("Planifier l'inspection commune préalable avec l'entreprise extérieure.");
  if (conflits.length) actions.push("Formaliser le phasage / les mesures de coactivité au plan de prévention.");
  permisRequis.forEach((p) => actions.push(`Préparer : ${PERMIS_LABEL[p] || p}.`));

  let statut = "compatible";
  if (crit.niveau === "critique" || conflits.length >= 2) statut = "bloquant";
  else if (crit.niveau === "elevee" || permisRequis.length || conflits.length || documentsManquants.length) statut = "sous_conditions";

  return {
    criticite: crit,
    statut,
    permisRequis: permisRequis.map((p) => ({ code: p, label: PERMIS_LABEL[p] || p })),
    risques,
    documentsManquants,
    vigilance,
    conflits,
    actions: [...new Set(actions)],
  };
}

export { PERMIS_LABEL };
